import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import {
  KeyAuditOperation,
  WebhookKey,
  WebhookKeyStatus,
  WebhookKeyWithPlain,
} from "../../types";
import {
  webhookKeyService,
  WebhookKeyUpdateBody,
} from "../../services/webhookKeyService";
import WebhookKeyForm, { WebhookKeyFormData } from "./WebhookKeyForm";
import WebhookKeyAuditLog from "./WebhookKeyAuditLog";
import WebhookKeyRevealModal from "./WebhookKeyRevealModal";

const STATUS_CHIP: Record<
  WebhookKeyStatus,
  { label: string; color: "success" | "warning" | "error" }
> = {
  active: { label: "Attiva", color: "success" },
  disabled: { label: "Disabilitata", color: "warning" },
  revoked: { label: "Revocata", color: "error" },
};

export default function WebhookKeyDetail() {
  const { keyId } = useParams<{ keyId: string }>();
  const navigate = useNavigate();

  const [key, setKey] = useState<WebhookKey | null>(null);
  const [auditLogs, setAuditLogs] = useState<KeyAuditOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [statusToggling, setStatusToggling] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateDisablePrevious, setRotateDisablePrevious] = useState(true);
  const [rotateLoading, setRotateLoading] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);

  const [freshKey, setFreshKey] = useState<WebhookKeyWithPlain | null>(null);

  const load = async () => {
    if (!keyId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await webhookKeyService.getById(keyId);
      setKey(res.data.data.key);
      setAuditLogs(res.data.data.auditLogs ?? []);
    } catch (e) {
      console.error(e);
      setLoadError("Impossibile caricare la chiave");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyId]);

  const formDefaults = useMemo<Partial<WebhookKeyFormData> | undefined>(
    () =>
      key
        ? {
            partnerName: key.partnerName,
            allowedEventTypes: key.allowedEventTypes ?? [],
            notes: key.notes ?? "",
          }
        : undefined,
    [key]
  );

  const isRevoked = key?.status === "revoked";

  const handleSaveMeta = async (data: WebhookKeyFormData) => {
    if (!keyId || !key) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const body: WebhookKeyUpdateBody = {
        partnerName: data.partnerName,
        allowedEventTypes: data.allowedEventTypes ?? [],
        notes: data.notes ?? "",
        // scopes: omesso intenzionalmente — il BE preserva il valore corrente (merge per-campo)
      };
      const res = await webhookKeyService.update(keyId, body);
      setKey(res.data.data);
      setSaveSuccess(true);
      // ricarica audit log per riflettere l'operazione "updated"
      await load();
    } catch (e: any) {
      console.error(e);
      const status = e?.response?.status;
      if (status === 409) {
        setSaveError("Impossibile aggiornare una chiave revocata.");
      } else if (status === 404) {
        setSaveError("Chiave non trovata.");
      } else {
        setSaveError("Errore durante il salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!keyId || !key || key.status === "revoked") return;
    const next: "active" | "disabled" =
      key.status === "active" ? "disabled" : "active";
    setStatusToggling(true);
    setStatusError(null);
    try {
      const res = await webhookKeyService.update(keyId, { status: next });
      setKey(res.data.data);
      await load();
    } catch (e) {
      console.error(e);
      setStatusError("Errore nell'aggiornamento dello stato");
    } finally {
      setStatusToggling(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!keyId) return;
    setRevokeLoading(true);
    setRevokeError(null);
    try {
      const res = await webhookKeyService.revoke(keyId);
      setKey(res.data.data);
      setRevokeOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      setRevokeError("Errore durante la revoca");
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleRotateConfirm = async () => {
    if (!keyId) return;
    setRotateLoading(true);
    setRotateError(null);
    try {
      const res = await webhookKeyService.rotate(keyId, {
        disablePrevious: rotateDisablePrevious,
      });
      const newKey = res.data.data;
      setRotateOpen(false);
      setFreshKey(newKey);
      // refresh dei metadati locali (la chiave corrente può essere stata disabilitata)
      await load();
    } catch (e) {
      console.error(e);
      setRotateError("Errore durante la rotazione");
    } finally {
      setRotateLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !key) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", py: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError ?? "Chiave non trovata"}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/egg/webhook-keys")}>
          Torna alla lista
        </Button>
      </Box>
    );
  }

  const status = STATUS_CHIP[key.status];

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", py: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/egg/webhook-keys")}
        >
          Lista chiavi
        </Button>
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Typography variant="h5" fontWeight={700}>
            {key.partnerName}
          </Typography>
          <Chip label={status.label} color={status.color} size="small" variant="outlined" />
        </Stack>
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {key.partnerId} · {key.keyPrefix} · {key.id}
        </Typography>
        {key.rotationOf && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Rotazione di:{" "}
            <Box
              component="a"
              sx={{ fontFamily: "monospace", cursor: "pointer", color: "primary.main" }}
              onClick={() => navigate(`/egg/webhook-keys/${key.rotationOf}`)}
            >
              {key.rotationOf}
            </Box>
          </Typography>
        )}
      </Box>

      {isRevoked && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            Chiave revocata
          </Typography>
          <Typography variant="caption">
            {key.revokedAt && `Revocata il ${new Date(key.revokedAt).toLocaleString("it-IT")}`}
            {key.revokedBy && ` da ${key.revokedBy}`}. Tutte le mutazioni
            sono disabilitate; le richieste in arrivo con questa chiave
            vengono rifiutate.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Metadati
        </Typography>
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Chiave aggiornata con successo
          </Alert>
        )}
        <WebhookKeyForm
          mode="edit"
          defaultValues={formDefaults}
          onSubmit={handleSaveMeta}
          isLoading={saving}
          submitLabel="Salva modifiche"
          submitError={saveError}
          disabled={isRevoked}
        />
      </Paper>

      {!isRevoked && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Stato
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexWrap: "wrap" }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={key.status === "active"}
                  disabled={statusToggling}
                  onChange={handleToggleStatus}
                />
              }
              label={key.status === "active" ? "Chiave attiva" : "Chiave disabilitata"}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="outlined" onClick={() => setRotateOpen(true)}>
              Ruota chiave
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setRevokeOpen(true)}
            >
              Revoca
            </Button>
          </Stack>
          {statusError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {statusError}
            </Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Cronologia operazioni
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Ultimi {auditLogs.length} eventi audit per questa chiave.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <WebhookKeyAuditLog logs={auditLogs} />
      </Paper>

      <Dialog
        open={revokeOpen}
        onClose={() => !revokeLoading && setRevokeOpen(false)}
      >
        <DialogTitle>Revoca chiave</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stai per revocare la chiave <strong>{key.partnerName}</strong>.
            Operazione <strong>irreversibile</strong>: tutte le richieste in
            arrivo con questa chiave verranno rifiutate. La propagazione
            cross-instance può richiedere fino a 60 secondi.
          </DialogContentText>
          {revokeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revokeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeOpen(false)} disabled={revokeLoading}>
            Annulla
          </Button>
          <Button
            onClick={handleRevokeConfirm}
            color="error"
            variant="contained"
            disabled={revokeLoading}
          >
            {revokeLoading ? "Revoca in corso..." : "Revoca"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rotateOpen}
        onClose={() => !rotateLoading && setRotateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ruota chiave</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Verrà generata una nuova chiave per <strong>{key.partnerName}</strong>.
            Il plaintext della nuova chiave sarà visibile{" "}
            <strong>una sola volta</strong>.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={rotateDisablePrevious}
                onChange={(e) => setRotateDisablePrevious(e.target.checked)}
              />
            }
            label="Disabilita immediatamente la chiave attuale (consigliato)"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", ml: 4 }}
          >
            Se deselezionato, la vecchia chiave resterà valida finché non la
            disabiliti manualmente — utile per gestire la transizione lato
            partner senza downtime.
          </Typography>
          {rotateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {rotateError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateOpen(false)} disabled={rotateLoading}>
            Annulla
          </Button>
          <Button
            onClick={handleRotateConfirm}
            variant="contained"
            disabled={rotateLoading}
          >
            {rotateLoading ? "Rotazione..." : "Ruota chiave"}
          </Button>
        </DialogActions>
      </Dialog>

      <WebhookKeyRevealModal
        keyData={freshKey}
        context="rotated"
        onClose={() => {
          const newId = freshKey?.id;
          setFreshKey(null);
          if (newId && newId !== keyId) {
            navigate(`/egg/webhook-keys/${newId}`);
          }
        }}
      />
    </Box>
  );
}
